import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";

const initialState = {
  sectorWorkflow: {
    step: "categories",
    data: {
      category: {},
      places: {},
      service: {},
      areas: [],
    },
  },

  jobStep: 0,
  client: null,
  jobServices: [],
  jobSelected: null,
  jobEventStartDate: dayjs().format(),
  jobEventEndDate: dayjs().format(),
  jobEventStartTime: dayjs().format(),
  jobEventEndTime: dayjs().format(),
  jobOperationStatus: {
    error: undefined,
    captured: undefined,
    message: "",
  },
  jobs: [],
  serviceType: "commercial",
  serviceList: [],
  jobConfiguration: {
    paymentRequired: false,
    benefits: "",
    workCompensation: "",
    tax: "",
    hasMileage: false,
    mileageRate: "",
    mileageDistance: 0,
    frequency: "ONCE",
    interval: 1,
    duration: "",
    grossProfit: "",
    averagePeople: "",
    staffRatePerHour: [],
    dates: [],
    supplies: [],
    contractOverride: [
      {
        startDate: dayjs().format(),
        endDate: dayjs().format(),
        hours: [
          {
            startHour: dayjs().add(1, "hour").set("minute", 0).set("second", 0).format(),
            endHour: dayjs().add(2, "hour").set("minute", 0).set("second", 0).format(),
          },
        ],
      },
      {
        startDate: dayjs().format(),
        endDate: dayjs().format(),
        hours: [
          {
            startHour: dayjs().add(1, "hour").set("minute", 0).set("second", 0).format(),
            endHour: dayjs().add(2, "hour").set("minute", 0).set("second", 0).format(),
          },
        ],
      },
      {
        startDate: dayjs().format(),
        endDate: dayjs().format(),
        hours: [
          {
            startHour: dayjs().add(1, "hour").set("minute", 0).set("second", 0).format(),
            endHour: dayjs().add(2, "hour").set("minute", 0).set("second", 0).format(),
          },
        ],
      },
    ],
    isFrequently: true,
    residentialAreas: {
      totalBedrooms: null,
      otherRooms: 0,
      masterBathrooms: null,
      regularBathrooms: null,
      halfBathrooms: null,
      kitchen: false,
      laundryRoom: false,
      livingRoom: false,
      stairs: false,
    },
    additionalRequests: {
      fridgeInteriorCleaning: false,
      fillAndRunDishwasher: false,
      tidyUp: false,
      windowInteriorCleaning: false,
      ovenInteriorCleaning: false,
      handDishWashing: false,
      garageCleaning: false,
      havePets: false,
    },
  },
  quoteRequestSchema: null,
  // Pagination
  jobsPagination: {
    page: 1,
    pageSize: 10,
    totalItems: 0,
  },
  refreshTrigger: 0,
};

export const jobStateSlice = createSlice({
  name: "jobState",
  initialState,
  reducers: {
    updateSectorWorkflow: (state, action) => {
      const { step, data } = action.payload;
      state.sectorWorkflow = {
        step,
        data: {
          ...state.sectorWorkflow.data,
          ...data,
        },
      };
    },

    updateAreaQuantity: (state, action) => {
      const { uid, quantity, tasks: payloadTasks = [] } = action.payload;
      const areas = state.sectorWorkflow?.data?.areas || [];
      const idx = areas.findIndex((a) => a.uid === uid);

      if (quantity <= 0) {
        state.sectorWorkflow.data.areas = areas.filter((a) => a.uid !== uid);
        return;
      }

      const newTasks = payloadTasks.length ? payloadTasks?.map((t) => ({ ...t, done: true })) : idx !== -1 ? areas[idx].tasks || [] : [];

      const newArea = { ...action.payload, quantity, tasks: newTasks };

      if (idx === -1) {
        state.sectorWorkflow.data.areas = [...areas, newArea];
      } else {
        state.sectorWorkflow.data.areas[idx] = newArea;
      }
    },

    updateAreaTask(state, action) {
      const { areaUid, taskId, done } = action.payload;
      const area = state.sectorWorkflow?.data?.areas?.find((a) => a.uid === areaUid);
      if (!area) return;

      if (!area.tasks) area.tasks = [];

      const existing = area.tasks.find((t) => t.id === taskId);
      if (existing) {
        existing.done = done;
      } else {
        area.tasks.push({ id: taskId, done });
      }
    },

    resetSectorWorkflow: (state) => {
      state.sectorWorkflow = {};
    },

    addJob: (state, action) => {
      state.jobs = [...state.jobs, action.payload];
    },

    deleteJob: (state, action) => {
      state.jobs = state.jobs.filter((job) => job?.id !== action.payload?.id);
    },

    updateJobs: (state, action) => {
      state.jobs = action.payload;
    },

    setJobState: (state, action) => {
      state.jobStep = action.payload > 0 ? action.payload : 0;
    },
    setClientState: (state, action) => {
      state.client = action.payload;
    },
    updateJobServices: (state, action) => {
      state.jobServices = action.payload;
    },

    updateJobServiceById: (state, action) => {
      state.jobServices = state.jobServices?.map((jobService) => {
        if (jobService.workServiceId === action.payload.id) {
          return {
            ...jobService,
            ...action.payload.jobService,
          };
        }
        return jobService;
      });
    },
    deleteJobServices: (state, action) => {
      state.jobServices = state.jobServices.filter((jobService) => jobService.id !== action.payload?.longitude);
    },
    resetJobServices: (state, action) => {
      state.jobServices = null;
    },

    updateJobEventStartTime: (state, action) => {
      state.jobEventStartTime = action.payload;
      state.jobEventEndTime = dayjs(action.payload).add(1, "hour").format();
    },

    updateJobEventEndTime: (state, action) => {
      state.jobEventEndTime = action.payload;
    },

    updateJobEventStartDate: (state, action) => {
      state.jobEventStartDate = action.payload;
    },
    updateJobEventEndDate: (state, action) => {
      state.jobEventEndDate = action.payload;
    },
    updateJobOperationStatus: (state, action) => {
      state.jobOperationStatus = action.payload;
    },
    clearJobOperationStatus: (state) => {
      state.jobOperationStatus = {
        error: undefined,
        captured: undefined,
        message: "",
      };
    },
    resetJobState: (state) => {
      const { jobs } = state;
      return { ...initialState, jobs };
    },
    updateJobSelected: (state, action) => {
      state.jobSelected = state.jobs.find((job) => job.id === action.payload);
    },
    resetJobSelected: (state) => {
      state.jobSelected = null;
    },
    updateServiceType: (state, action) => {
      state.serviceType = action.payload;
    },
    updateServiceList: (state, action) => {
      state.serviceList = action.payload;
    },
    updateJobConfiguration: (state, action) => {
      state.jobConfiguration = {
        ...state.jobConfiguration,
        ...action.payload,
      };
    },
    updateJobContractOverride: (state, action) => {
      const { index, subIndex, field, value } = action.payload;

      if (typeof subIndex === "number") {
        // Actualización dentro de hours (anidado)
        if (state.jobConfiguration.contractOverride[index]?.hours[subIndex]) {
          state.jobConfiguration.contractOverride[index].hours[subIndex][field] = value;
        }
      } else {
        // Actualización directa (startDate, endDate)
        state.jobConfiguration.contractOverride[index][field] = value;
      }
    },
    updateJobQuoteSchema: (state, action) => {
      state.quoteRequestSchema = action.payload;
    },
    jobResetInitialState: (state) => {
      state.jobStep = 0;
      state.jobServices = [];
      state.client = null;
      state.jobEventStartDate = dayjs().format();
      state.jobEventEndDate = dayjs().format();
      state.jobEventStartTime = dayjs().format();
      state.jobEventEndTime = dayjs().format();
      state.jobOperationStatus = {
        error: undefined,
        captured: undefined,
        message: "",
      };
      state.serviceType = "commercial";
      state.serviceList = [];
      state.jobConfiguration = JSON.parse(JSON.stringify(initialState.jobConfiguration));
      state.quoteRequestSchema = null;
    },
    resetJobConfiguration: (state) => {
      state.jobConfiguration = initialState.jobConfiguration;
    },
    updateJobPagination: (state, action) => {
      state.jobsPagination = {
        ...state.jobsPagination,
        ...action.payload,
      };
    },
    updateResidentialAreas: (state, action) => {
      state.jobConfiguration.residentialAreas = {
        ...state.jobConfiguration.residentialAreas,
        ...action.payload,
      };
    },
    updateAdditionalRequests: (state, action) => {
      state.jobConfiguration.additionalRequests = {
        ...state.jobConfiguration.additionalRequests,
        ...action.payload,
      };
    },
    triggerJobRefresh: (state) => {
      state.refreshTrigger += 1; // cambia el valor para forzar el refresh
    },
  },
});

export const {
  updateJobEventStartDate,
  updateJobEventEndDate,
  setJobState,
  setClientState,
  updateJobServices,
  resetJobServices,
  updateJobEventStartTime,
  updateJobEventEndTime,
  updateJobServiceById,
  updateJobOperationStatus,
  resetJobState,
  addJob,
  deleteJob,
  updateJobs,
  updateJobSelected,
  resetJobSelected,
  updateServiceType,
  updateServiceList,
  updateJobConfiguration,
  updateJobContractOverride,
  updateJobQuoteSchema,
  jobResetInitialState,
  updateJobPagination,
  resetJobConfiguration,
  updateResidentialAreas,
  updateAdditionalRequests,
  clearJobOperationStatus,

  updateSectorWorkflow,
  updateAreaQuantity,
  updateAreaTask,
  resetSectorWorkflow,
  triggerJobRefresh,
} = jobStateSlice.actions;

export default jobStateSlice.reducer;
